import { Classes, Icon, TextArea } from "@blueprintjs/core"
import { FileLink } from "modules/common/components/files/FileLink"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { DATE_TIME_FORMAT, EMPTY_OBJECT_ID } from "modules/common/constants"
import { asFileBase } from "modules/common/models/file-metadata"
import { MessageType } from "modules/orders-manage/models/order-question"
import { observer } from "mobx-react"
import moment from "moment"
import React, { useCallback, useRef, useState } from "react"
import styles from "./QuestionFeed.module.scss"

const initials = (name: string) => {
  const parts = name.split(" ").filter((p) => !!p)

  if (!parts.length) {
    return "?"
  }

  return `${parts[0][0] || ""}${parts.length > 1 ? parts[1][0] : ""}`.toUpperCase()
}

const noop = () => undefined

const FILTERS = [
  { id: "all", label: "Все" },
  { id: "results", label: "Результаты" },
  { id: "comments", label: "Комментарии" },
]

/**
 * Обсуждение задания: комментарии и результаты работы одной лентой, по умолчанию новые сверху;
 * порядок переключается кнопкой со стрелками у заголовка.
 * Результат — комментарий с пометкой: его отправка отметила автору готовность
 */
export const QuestionFeed: React.FC<QuestionFeedProps> = observer(({ messages, currentUserId, baseUrl, onEdit, onRemove }) => {
  const [filter, setFilter] = useState("all")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [busy, setBusy] = useState(false)
  const [newestFirst, setNewestFirst] = useState(true)

  // с сервера лента приходит от новых к старым; «сначала старые» — перевёрнутая.
  // Без useMemo: лента — массив MST, он меняется на месте, ссылка та же, а узлы новые
  const ordered = newestFirst ? messages.slice() : messages.slice().reverse()
  const results = ordered.filter((m) => m.isResult).length

  const shown = ordered.filter((m) => (filter === "results" ? m.isResult : filter === "comments" ? !m.isResult : true))

  const saveEdit = useCallback(
    async (message: MessageType) => {
      const value = editText.trim()

      if (!value && message.files.length === 0) {
        return
      }

      setBusy(true)
      await onEdit({ ...message, description: value })
      setBusy(false)
      setEditingId(null)
      setEditText("")
    },
    [editText, onEdit]
  )

  return (
    <div className={styles.feed}>
      <div className={styles.head}>
        <h3>Обсуждение</h3>

        {messages.length > 1 && (
          <button
            type="button"
            className={`${styles.sort} ${newestFirst ? "" : styles.sortAsc}`}
            title={newestFirst ? "Сначала новые — показать сначала старые" : "Сначала старые — показать сначала новые"}
            aria-label={newestFirst ? "Сначала новые" : "Сначала старые"}
            onClick={() => setNewestFirst(!newestFirst)}
          >
            <Icon icon="swap-vertical" size={14} />
          </button>
        )}

        {messages.length > 0 && (
          <div className={styles.tabs} role="tablist">
            {FILTERS.map((f) => {
              const count = f.id === "all" ? ordered.length : f.id === "results" ? results : ordered.length - results

              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`${styles.tab} ${filter === f.id ? styles.tabOn : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label} · {count}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {shown.map((message, index) => {
        const own = !message.employer || message.employer.id === currentUserId
        const name = message.employer ? message.employer.name : "Вы"
        const key = message.id || `new-${index}`
        const editing = editingId === key

        return (
          <div className={styles.item} key={key}>
            <span className={`${styles.avatar} ${own ? styles.ownAvatar : ""}`}>{initials(name)}</span>

            <div className={`${styles.bubble} ${message.isResult ? styles.result : ""}`}>
              <div className={styles.meta}>
                <span className={styles.author}>{name}</span>
                {message.isResult && (
                  <span className={styles.resultBadge}>
                    <GeneralIcon type="general-checkbox" size={11} />
                    Результат
                  </span>
                )}
                <span className={styles.date}>{!message.day ? message.date : `${message.day} · ${message.hours}`}</span>

                {own && !editing && (
                  <span className={styles.actions}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(key)
                        setEditText(message.description)
                      }}
                    >
                      Изменить
                    </button>
                    <button type="button" onClick={() => onRemove(message, messages.indexOf(message))}>
                      Удалить
                    </button>
                  </span>
                )}
              </div>

              {editing ? (
                <div className={styles.editRow}>
                  <TextArea
                    value={editText}
                    autoFocus={true}
                    autoResize={true}
                    className={Classes.FILL}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <div className={styles.editButtons}>
                    <PlanrButton type="greenish" size="small" onClick={() => saveEdit(message)} disabled={busy}>
                      Сохранить
                    </PlanrButton>
                    <PlanrButton
                      type="graybtn"
                      size="small"
                      onClick={() => {
                        setEditingId(null)
                        setEditText("")
                      }}
                    >
                      Отмена
                    </PlanrButton>
                  </div>
                </div>
              ) : (
                message.description && <div className={styles.text}>{message.description}</div>
              )}

              {message.files.length > 0 && (
                <div className={styles.files}>
                  {message.files.map((file) => (
                    <FileLink key={file.id} baseUrl={baseUrl} file={asFileBase(file)} readOnly={true} onRemove={noop} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}

      {messages.length === 0 && <div className={styles.empty}>Пока никто ничего не написал</div>}
      {messages.length > 0 && shown.length === 0 && (
        <div className={styles.empty}>{filter === "results" ? "Результатов пока нет" : "Комментариев пока нет"}</div>
      )}
    </div>
  )
})

/**
 * Поле комментария: текст, файлы и пометка «Это результат». Результат отмечает
 * готовность автора — галочка видна исполнителю, который ещё не отметился
 */
export const QuestionComposer: React.FC<QuestionComposerProps> = ({ canAttach, canResult, upload, baseUrl, onSend }) => {
  const [text, setText] = useState("")
  const [files, setFiles] = useState<FileBase[]>([])
  const [isResult, setIsResult] = useState(false)
  const [busy, setBusy] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  const attach = useCallback(
    async (list: FileList | null) => {
      if (!list || list.length === 0) {
        return
      }

      setBusy(true)
      for (const file of Array.from(list)) {
        const uploaded = await upload(file)
        uploaded && setFiles((prev) => [...prev, uploaded])
      }
      setBusy(false)
    },
    [upload]
  )

  const send = useCallback(async () => {
    const value = text.trim()

    if (!value && files.length === 0) {
      return
    }

    setBusy(true)
    const ok = await onSend(
      {
        id: EMPTY_OBJECT_ID,
        description: value,
        employer: null,
        day: "",
        hours: "",
        date: moment(new Date()).format(DATE_TIME_FORMAT),
        files: [],
        isResult: canResult && isResult,
      } as unknown as MessageType,
      files
    )
    setBusy(false)

    if (ok !== false) {
      setText("")
      setFiles([])
      setIsResult(false)
    }
  }, [text, files, isResult, canResult, onSend])

  return (
    <div
      className={`${styles.composer} ${dragOver ? styles.dragOver : ""}`}
      onDragOver={(e) => {
        if (!canAttach) {
          return
        }

        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!canAttach) {
          return
        }

        e.preventDefault()
        setDragOver(false)
        attach(e.dataTransfer.files)
      }}
    >
      <TextArea
        value={text}
        className={`${Classes.FILL} ${styles.input}`}
        autoResize={true}
        aria-label="Комментарий"
        placeholder={canAttach ? "Написать… файлы можно перетащить сюда" : "Написать комментарий…"}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            send()
          }
        }}
      />

      {files.length > 0 && (
        <div className={styles.files}>
          {files.map((file) => (
            <FileLink
              key={file.fileId}
              baseUrl={baseUrl}
              file={file}
              onRemove={(removed: FileBase) => setFiles((prev) => prev.filter((f) => f.fileId !== removed.fileId))}
            />
          ))}
        </div>
      )}

      <div className={styles.composerBar}>
        {canAttach && (
          <>
            <input
              ref={picker}
              type="file"
              multiple={true}
              hidden={true}
              onChange={(e) => {
                attach(e.target.files)
                e.target.value = ""
              }}
            />
            <button
              type="button"
              className={styles.attach}
              title="Прикрепить файлы"
              onClick={() => picker.current && picker.current.click()}
            >
              <GeneralIcon type="general-attach" size={14} />
              Файл
            </button>
          </>
        )}

        {canResult && (
          // отметка в стиле приложения: иконки planr вместо галочки браузера
          <button
            type="button"
            role="checkbox"
            aria-checked={isResult}
            className={styles.resultToggle}
            onClick={() => setIsResult(!isResult)}
          >
            <GeneralIcon type={isResult ? "general-checkbox" : "general-zero-checkbox"} />
            Это результат — отметить выполненным
          </button>
        )}

        {/* «Отмена» и «Отправить» — одной группой справа: переносятся только вместе */}
        <span className={styles.sendGroup}>
          {/* отмена — только когда есть что отменять: текст, файлы или пометка результата */}
          {(text || files.length > 0 || isResult) && (
            <PlanrButton
              type="graybtn"
              size="small"
              disabled={busy}
              onClick={() => {
                setText("")
                setFiles([])
                setIsResult(false)
              }}
            >
              Отмена
            </PlanrButton>
          )}
          <PlanrButton
            type="primary"
            size="small"
            title="Отправить — Ctrl + Enter"
            onClick={send}
            disabled={busy || (!text.trim() && files.length === 0)}
          >
            Отправить
          </PlanrButton>
        </span>
      </div>
    </div>
  )
}

interface QuestionFeedProps {
  messages: MessageType[]
  currentUserId: string
  baseUrl: string
  onEdit: (message: MessageType) => Promise<any>
  onRemove: (message: MessageType, index: number) => void
}

interface QuestionComposerProps {
  /** Файлы прикладывают к сохранённому заданию: у нового их некуда загрузить */
  canAttach: boolean
  /** Пользователь — исполнитель, ещё не отметивший готовность */
  canResult: boolean
  baseUrl: string
  upload: (file: File) => Promise<FileBase | null>
  onSend: (message: MessageType, files: FileBase[]) => Promise<any>
}
